/**
 * The bindings that bind no field (fix/widgets).
 *
 * Six keys travel inside `bindings` without naming a column —`time`,
 * `comparisons`, `point_mode`, `source_mode`, `relationships`,
 * `context_queries`— and each has a shape of its own, so the generic field
 * editor leaves them alone (see `widgetContract.NON_FIELD_BINDING_SLOTS`).
 * Leaving them *out of the form* as well is what made the builder draw two of
 * KPI's four binding keys and say nothing about the other two.
 *
 * This decides what the editor can do with each one, from the slot's own
 * schema rather than from a list of keys:
 *
 * | Support | What it is | Who |
 * |---|---|---|
 * | `time` | the period: a date column of the source plus a range | 25 types |
 * | `enum` | a single choice with a default, like any `display` select | `point_mode`, `source_mode` |
 * | `unsupported` | an object or a list with a shape of its own | `comparisons`, `relationships`, `context_queries` |
 *
 * An unsupported slot is **shown, not hidden**: the author sees the field the
 * catalog declares and a note saying the builder cannot author it yet, which
 * is the difference between a gap someone can report and a gap nobody can
 * see. None of the six is required by any type, so none of them blocks a save.
 */

import { buildTimeBinding, parseTimeBinding } from './widgetTimeBinding.js';

export const NON_FIELD_SUPPORT = Object.freeze({
  TIME: 'time',
  ENUM: 'enum',
  UNSUPPORTED: 'unsupported',
});

/** The date types a `time` binding's column may have. */
export const TIME_DATA_TYPES = Object.freeze(['DATE', 'DATETIME', 'TIMESTAMP']);

/** The choices a non-field slot offers, when it is a plain enum. */
export const enumOf = (slot) => (Array.isArray(slot?.node?.enum) ? slot.node.enum : null);

/** What the editor can do with this slot. */
export function supportFor(slot) {
  if (slot?.key === 'time') return NON_FIELD_SUPPORT.TIME;
  if (enumOf(slot)?.length) return NON_FIELD_SUPPORT.ENUM;
  return NON_FIELD_SUPPORT.UNSUPPORTED;
}

/**
 * The form's non-field state → the keys that go inside `bindings`.
 *
 * @param {object} values `{ time: { ref, range, followDashboardDateFilter },
 *   point_mode: 'X', … }` as the form holds it — `time.ref` points at a pool
 *   item, because the contract wants the alias the generated dataset carries.
 * @param {(ref: string) => string} aliasOf Resolves that ref to its alias.
 * @returns {object} Only the keys that resolved to something valid; an
 *   incomplete `time` is left out rather than sent half-built.
 */
export function buildNonFieldBindings(values = {}, aliasOf = () => '') {
  const built = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key === 'time') {
      const time = buildTimeBinding({
        field: aliasOf(value?.ref),
        range: value?.range,
        followDashboardDateFilter: value?.followDashboardDateFilter,
      });
      if (time) built.time = time;
      return;
    }
    if (typeof value === 'string' && value) built[key] = value;
  });

  return built;
}

/**
 * A saved `bindings` → the non-field state the form holds.
 *
 * `time.field` comes back as the alias it was sent as, and the form keeps a
 * ref, so the caller resolves it the other way round.
 *
 * @param {object|null} bindings The stored bindings.
 * @param {(alias: string) => string} refOf Alias → pool ref.
 */
export function parseNonFieldBindings(bindings, refOf = () => '') {
  if (!bindings || typeof bindings !== 'object') return {};

  const values = {};
  const time = parseTimeBinding(bindings);
  if (time) {
    values.time = {
      ref: refOf(time.field) || '',
      range: time.range,
      followDashboardDateFilter: time.followDashboardDateFilter,
    };
  }
  Object.entries(bindings).forEach(([key, value]) => {
    if (key !== 'time' && typeof value === 'string') values[key] = value;
  });

  return values;
}

export default buildNonFieldBindings;
